import React, { useState } from 'react';
import dayjs from 'dayjs';
import { AddCourierModal } from '../AddCourierModal/AddCourierModal';
import { AddMultipleCourierModal } from '../AddMultipleCourierModal/AddMultipleCourierModal';
import { AddOpenSpotButton } from '../AddOpenSpotButton/AddOpenSpotButton';
import { CourierDayList } from './CourierDayList/CourierDayList';
import { addCourierToShift, addMultipleCourierToShift } from '../updateShift';
import { addExtraWorkToExistingShift } from '../updateExtraWork';
import {
  ShiftCourierJson,
  ShiftStatusType,
  ShiftCourierWithDateJson,
  UpdateType,
} from '../../../const/ShiftConst';
import './WeekDayForAdmin.scss';
import { CourierJson, TravelMode } from '../../../types/bundleTypes';
import { Dayjs } from 'dayjs';
import { SmsCourierModal } from '../SmsCourierModal/SmsCourierModal';
import { FaSms } from 'react-icons/fa';
import { AddExtraWorkModal } from '../AddExtraWorkModal/AddExtraWorkModal';
import { GoPackage } from 'react-icons/go';
import { Region } from '../../../types/orderTypes';
import { TooltipComponent } from '../../../ReusableComponents/Tooltip/TooltipComponent';
import { ExtraWork } from '../../new-shift-planner/shiftApi';
import { filterShiftsWithStatus } from 'utils/shifts';

const { BOOKED } = ShiftStatusType;

function filterAvailableShifts(
  shiftCourierList: ShiftCourierJson[]
): ShiftCourierJson[] {
  return shiftCourierList
    .filter(shiftCourier => shiftCourier.status !== ShiftStatusType.DELETED)
    .filter(shiftCourier => shiftCourier.status !== ShiftStatusType.WISH)
    .filter(shiftCourier => shiftCourier.status !== ShiftStatusType.CANCELED)
    .filter(shiftCourier => shiftCourier.status !== ShiftStatusType.DECLINED);
}

function extractBookedCourierIds(shifts: ShiftCourierJson[]): number[] {
  const bookedShifts = filterShiftsWithStatus(shifts, BOOKED);
  return [...new Set(bookedShifts.map(shift => shift.courierId))];
}

function bookedShiftScannerCourierList(shifts: ShiftCourierJson[]) {
  const bookedShifts = filterShiftsWithStatus(shifts, BOOKED);
  const filterSortExtra = (obj: ExtraWork) => obj.typeOfExtraWork === 'SORT';
  const filterExtraWork = (obj: ShiftCourierJson) =>
    obj.extraWork.filter(filterSortExtra).length > 0;

  return bookedShifts.filter(filterExtraWork);
}

function calcNrOfBookedSpotsShuldHaveWarning(
  shiftCourierList: ShiftCourierJson[],
  nrOfAvailableBookings: number
): string {
  if (nrOfAvailableBookings > 0) {
    const bookedShifts = filterShiftsWithStatus(
      shiftCourierList,
      BOOKED
    ).length;
    const uniqueBookedShifts = extractBookedCourierIds(shiftCourierList).length;
    const bookedShiftsDiff = bookedShifts - uniqueBookedShifts;
    return bookedShifts === uniqueBookedShifts ? '' : `⚠️ ${bookedShiftsDiff} `;
  } else {
    return '';
  }
}

type TravelTypeBookedSpot = { booked: number; available: number };

function populateTravelTypeSpots(
  bookedShifts: ShiftCourierJson[],
  availableShifts: ShiftCourierJson[],
  travelMode: TravelMode
): TravelTypeBookedSpot {
  const filterByTravelMode = (list: ShiftCourierJson[], mode: TravelMode) =>
    list.filter(shift => shift.shiftTravelMode === mode).length;

  return {
    booked: filterByTravelMode(bookedShifts, travelMode),
    available: filterByTravelMode(availableShifts, travelMode),
  };
}

function calcNrOfTraveltypeBookedSpots(shifts: ShiftCourierJson[]): {
  nrOfCar: TravelTypeBookedSpot;
  nrOfBike: TravelTypeBookedSpot;
} {
  const { CAR, BIKE } = TravelMode;
  const bookedShifts = filterShiftsWithStatus(shifts, BOOKED);
  const availableShifts = filterAvailableShifts(shifts);
  const nrOfCar = populateTravelTypeSpots(bookedShifts, availableShifts, CAR);
  const nrOfBike = populateTravelTypeSpots(bookedShifts, availableShifts, BIKE);

  return { nrOfCar, nrOfBike };
}

const WeekDayHeader: React.FC<{
  toggleExtraWorkModal: () => void;
  toggleSmsCourierModal: () => void;
  shiftCourierList: ShiftCourierJson[];
  date: Dayjs;
}> = ({
  toggleExtraWorkModal,
  toggleSmsCourierModal,
  shiftCourierList,
  date,
}) => {
  return (
    <div>
      <div className="weekAndMenu">
        <div className="menu">
          <div onClick={toggleExtraWorkModal}>
            <div className="symbol">
              <TooltipComponent
                id="extraWorkToolTip"
                title="Add extra work to shifts"
              >
                <GoPackage style={{ fontSize: '1.5rem' }} />
              </TooltipComponent>
            </div>
          </div>
        </div>

        <WeekDayBox date={date} />
        <div className="menu">
          <div onClick={toggleSmsCourierModal}>
            <div className="symbol">
              <TooltipComponent id="smsToolTip" title="Get phone numbers">
                <FaSms style={{ fontSize: '1.5rem' }} />
              </TooltipComponent>
            </div>
          </div>
        </div>
      </div>
      <DayBookedStats shiftCourierList={shiftCourierList ?? []} />
    </div>
  );
};

export function DayBookedStats({
  shiftCourierList,
}: {
  shiftCourierList: ShiftCourierJson[];
}): JSX.Element {
  const nrOfAvialableBookings: number =
    filterAvailableShifts(shiftCourierList).length;
  const nrOfBookedSpots = extractBookedCourierIds(shiftCourierList).length;
  const warningText: string = calcNrOfBookedSpotsShuldHaveWarning(
    shiftCourierList,
    nrOfAvialableBookings
  );
  const nrOfBookedScanningSpots =
    bookedShiftScannerCourierList(shiftCourierList).length;

  return (
    <>
      {warningText && (
        <div className="dayBookedStatsBox">
          <div className="statsText">{`${warningText}Duplicate`}</div>
        </div>
      )}
      <div className="dayBookedStatsBox">
        <div className="statsText">
          {`${nrOfBookedSpots}/${nrOfAvialableBookings}`}
        </div>
      </div>
      <div className="dayBookedStatsBox">
        <div className="statsText">{`${nrOfBookedScanningSpots}`}</div>
      </div>
    </>
  );
}

export function DayBookedStatsForDashboardWidget({
  shiftCourierList,
}: {
  shiftCourierList: ShiftCourierJson[];
}): JSX.Element {
  const nrOfAvialableBookings = filterAvailableShifts(shiftCourierList).length;
  const nrOfBookedSpots = extractBookedCourierIds(shiftCourierList).length;
  const { nrOfCar, nrOfBike } = calcNrOfTraveltypeBookedSpots(shiftCourierList);

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          fontSize: '1.6rem',
        }}
      >
        {`${nrOfBookedSpots}/${nrOfAvialableBookings}`}
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          fontSize: '1.4rem',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          {`Bike:   ${nrOfBike.booked}/${nrOfBike.available}`}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          {`Car:    ${nrOfCar.booked}/${nrOfCar.available}`}
        </div>
      </div>
    </div>
  );
}

export function WeekDayBox({ date }: { date: Dayjs }): JSX.Element {
  return (
    <div className="weekDayBox">
      <div className="weekdayName">{dayjs(date).format('ddd')}</div>
      <div className="weekdayDate">{dayjs(date).format(' DD/MM')}</div>
    </div>
  );
}

export function WeekDayForAdmin({
  courierListBookedObj,
  addShiftCourierNextDayMethod,
  updateShiftCourierListForDay,
  region,
}: {
  courierListBookedObj: ShiftCourierWithDateJson;
  addShiftCourierNextDayMethod: (shiftCourier: ShiftCourierJson) => void;
  updateShiftCourierListForDay: (
    shiftCourierList: ShiftCourierJson[],
    date: Dayjs,
    updateType: UpdateType
  ) => void;
  region: Region;
}): JSX.Element {
  const { courierDayBooked } = courierListBookedObj;
  const date: Dayjs = dayjs(courierListBookedObj.date);
  const [showAddShift, setShowAddShift] = useState<boolean>(false);
  const [showAddMultipleCoruier, setShowAddMultipleCoruier] = useState(false);
  const [showSmsCourierModal, setShowSmsCourierModal] = useState(false);
  const [showExtraWorkShiftModal, setShowExtraWorkShiftModal] = useState(false);
  const [selectedCourierShiftIndex, setSelectedCourierShiftIndex] =
    useState<number>(null);

  const closeModal = () => {
    setShowAddShift(false);
  };

  const closeMultipleModal = () => {
    setShowAddMultipleCoruier(false);
  };

  async function updateShiftValues(selectedCourier: CourierJson) {
    const newShiftCourierList: ShiftCourierJson[] = await addCourierToShift(
      courierDayBooked,
      selectedCourier,
      selectedCourierShiftIndex
    );

    updateShiftCourierListForDay(newShiftCourierList, date, UpdateType.REPLACE);
  }

  async function updateMultipleShiftValues(
    selectedCourierList: CourierJson[],
    day: Dayjs,
    region: Region
  ): Promise<void> {
    const newShiftCourierList = await addMultipleCourierToShift(
      courierDayBooked,
      selectedCourierList,
      day,
      region
    );
    if (courierDayBooked.length > 0) {
      updateShiftCourierListForDay(
        newShiftCourierList,
        date,
        UpdateType.REPLACE
      );
    } else {
      updateShiftCourierListForDay(newShiftCourierList, date, UpdateType.ADD);
    }
  }

  async function updateShiftsForExtraWork(
    selectedShiftsToUpdateList: ShiftCourierJson[]
  ): Promise<void> {
    const newShiftCourierList = await addExtraWorkToExistingShift(
      courierDayBooked,
      selectedShiftsToUpdateList
    );
    if (newShiftCourierList.length > 0) {
      updateShiftCourierListForDay(
        newShiftCourierList,
        date,
        UpdateType.REPLACE
      );
    }
  }

  return (
    <div className="weekdayForAdmin">
      <WeekDayHeader
        toggleExtraWorkModal={() => setShowExtraWorkShiftModal(prev => !prev)}
        toggleSmsCourierModal={() => setShowSmsCourierModal(prev => !prev)}
        shiftCourierList={courierDayBooked}
        date={date}
      />
      <CourierDayList
        shiftCourierList={courierDayBooked ?? []}
        setShowAddShift={setShowAddShift}
        showAddShift={showAddShift}
        setSelectedCourierShiftIndex={setSelectedCourierShiftIndex}
        addShiftCourierNextDayMethod={addShiftCourierNextDayMethod}
        date={date}
        UpdateShiftCourierListForDay={updateShiftCourierListForDay}
      />
      <AddOpenSpotButton
        date={date}
        UpdateShiftCourierListForDay={updateShiftCourierListForDay}
        region={region}
      />
      <div
        className="addMultipleCouriersButton"
        onClick={() => setShowAddMultipleCoruier(prev => !prev)}
      >
        <div className="nameText">+ multiple couriers</div>
      </div>
      {showAddShift && (
        <AddCourierModal
          date={date}
          closeModal={closeModal}
          updateShiftValues={updateShiftValues}
          shiftCourierList={courierDayBooked}
        />
      )}
      {showAddMultipleCoruier && (
        <AddMultipleCourierModal
          date={date}
          closeModal={closeMultipleModal}
          updateMultipleShiftValues={updateMultipleShiftValues}
          shiftCourierList={courierDayBooked}
          region={region}
        />
      )}
      {showSmsCourierModal && (
        <SmsCourierModal
          date={date}
          closeModal={() => setShowSmsCourierModal(false)}
          shiftCourierList={courierDayBooked}
          region={region}
        />
      )}
      {showExtraWorkShiftModal && (
        <AddExtraWorkModal
          date={date}
          closeModal={() => setShowExtraWorkShiftModal(false)}
          updateMultipleShiftValuesForExtraWork={updateShiftsForExtraWork}
          shiftCourierList={courierDayBooked.filter(
            o => o.status === ShiftStatusType.BOOKED
          )}
        />
      )}
    </div>
  );
}
